<template>
  <div>
    <v-card class="rounded-lg elevation-1">
      <v-card-title class="d-flex align-center py-3 px-4">
        <v-icon color="primary" class="me-2">
          mdi-poll
        </v-icon>
        <span class="text-h6 font-weight-bold primary--text">{{$t('reservations')}}</span>
      </v-card-title>

      <!-- Advanced Filter Bar (Top of Reservations View) -->
      <v-card outlined class="mx-4 my-2 pa-3 grey lighten-5 rounded-lg border-grey">
        <v-row dense align="center">
          <!-- Customer Search -->
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="filters.customer_search"
              label="بحث باسم العميل أو رقم الهاتف"
              dense
              outlined
              hide-details
              clearable
              prepend-inner-icon="mdi-account-search"
              @keyup.enter="applyFilters"
            ></v-text-field>
          </v-col>

          <!-- Driver Search -->
          <v-col cols="12" sm="6" md="3">
            <v-text-field
              v-model="filters.driver_id"
              label="بحث باسم السائق أو رقم الهاتف"
              dense
              outlined
              hide-details
              clearable
              prepend-inner-icon="mdi-steering"
              @keyup.enter="applyFilters"
            ></v-text-field>
          </v-col>

          <!-- Date From -->
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model="filters.date_from"
              type="date"
              label="من تاريخ"
              dense
              outlined
              hide-details
            ></v-text-field>
          </v-col>

          <!-- Date To -->
          <v-col cols="12" sm="6" md="2">
            <v-text-field
              v-model="filters.date_to"
              type="date"
              label="إلى تاريخ"
              dense
              outlined
              hide-details
            ></v-text-field>
          </v-col>

          <!-- Action Buttons & Quick Filter -->
          <v-col cols="12" md="2" class="d-flex align-center gap-1 justify-end">
            <v-btn color="primary" small class="font-weight-bold" :loading="isLoading" @click="applyFilters">
              <v-icon left size="16">mdi-filter</v-icon>
              تصفية
            </v-btn>
            <v-btn color="grey darken-1" text small class="font-weight-bold" @click="resetFilters">
              <v-icon left size="16">mdi-refresh</v-icon>
              إعادة ضبط
            </v-btn>
          </v-col>
        </v-row>

        <!-- Quick Date Filter Buttons -->
        <v-row dense class="mt-2 align-center">
          <v-col cols="12" class="d-flex align-center">
            <span class="text-caption font-weight-bold grey--text text--darken-2 me-2">تصفية سريعة:</span>
            <v-btn
              x-small
              depressed
              :color="quickFilter === 'today' ? 'primary' : 'grey lighten-3'"
              :class="quickFilter === 'today' ? 'white--text' : 'grey--text text--darken-2'"
              class="me-1 font-weight-bold"
              @click="setQuickFilter('today')"
            >
              اليوم
            </v-btn>
            <v-btn
              x-small
              depressed
              :color="quickFilter === 'tomorrow' ? 'primary' : 'grey lighten-3'"
              :class="quickFilter === 'tomorrow' ? 'white--text' : 'grey--text text--darken-2'"
              class="me-1 font-weight-bold"
              @click="setQuickFilter('tomorrow')"
            >
              غداً
            </v-btn>
            <v-btn
              x-small
              depressed
              :color="quickFilter === 'all' ? 'primary' : 'grey lighten-3'"
              :class="quickFilter === 'all' ? 'white--text' : 'grey--text text--darken-2'"
              class="font-weight-bold"
              @click="setQuickFilter('all')"
            >
              جميع التواريخ
            </v-btn>
          </v-col>
        </v-row>
      </v-card>

      <!-- Tabs Navigation -->
      <v-tabs v-model="active_tab" show-arrows class="my-2 px-2">
        <v-tab v-for="tab in translatedTabs" :key="tab.idx" class="font-weight-bold">
          <v-icon size="20" class="me-2">
            {{ tab.icon }}
          </v-icon>
          <span>{{ tab.title }}</span>
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="active_tab">
        <!-- active / upcoming -->
        <v-tab-item>
          <reservations-table
            :show-cancel="true"
            :loading="isLoading"
            :reservations="activeReservations"
            @cancel-reservation="cancelReservation"
            @open-customer-profile="openCustomerModal"
          ></reservations-table>
        </v-tab-item>

        <!-- ride -->
        <v-tab-item>
          <reservations-table
            :show-cancel="true"
            :loading="isLoading"
            @cancel-reservation="cancelReservation"
            @open-customer-profile="openCustomerModal"
            :reservations="rideReservations"
          ></reservations-table>
        </v-tab-item>

        <!-- missed -->
        <v-tab-item>
          <reservations-table
            :show-cancel="true"
            :loading="isLoading"
            @cancel-reservation="cancelReservation"
            @open-customer-profile="openCustomerModal"
            :reservations="missedReservations"
          ></reservations-table>
        </v-tab-item>

        <!-- completed -->
        <v-tab-item>
          <reservations-table
            :show-cancel="true"
            :loading="isLoading"
            @cancel-reservation="cancelReservation"
            @open-customer-profile="openCustomerModal"
            :reservations="completedReservations"
          ></reservations-table>
        </v-tab-item>

        <!-- cancelled -->
        <v-tab-item>
          <reservations-table
            :show-cancel="false"
            :loading="isLoading"
            @open-customer-profile="openCustomerModal"
            :reservations="cancelledReservations"
          ></reservations-table>
        </v-tab-item>

        <!-- no-shows -->
        <v-tab-item>
          <no-shows-table></no-shows-table>
        </v-tab-item>
      </v-tabs-items>
    </v-card>

    <!-- Customer Profile Modal -->
    <customer-profile-modal
      v-model="showCustomerModal"
      :customer="selectedCustomer"
    ></customer-profile-modal>
  </div>
</template>

<script>

import {
  mdiStopCircleOutline,
  mdiAccountCheck,
  mdiAccountClock,
  mdiAccountOff,
  mdiPlayCircleOutline,
  mdiTrashCan,
  mdiDeleteRestore,
  mdiAirplane,
  mdiMotionPause,
  mdiAccountCancelOutline
} from "@mdi/js";

import reservationsTable from './reservations-table.vue';
import noShowsTable from './no-shows-table.vue';
import CustomerProfileModal from './CustomerProfileModal.vue';

export default {
  components: {
    reservationsTable,
    noShowsTable,
    CustomerProfileModal
  },
  data() {
    return {
      activeReservations: [],
      rideReservations: [],
      completedReservations: [],
      missedReservations: [],
      cancelledReservations: [],
      isLoading: false,
      selectedReservation: null,
      search: "",
      filters: {
        customer_search: "",
        driver_id: "",
        date_from: "",
        date_to: ""
      },
      quickFilter: "all",
      showCustomerModal: false,
      selectedCustomer: null,
      tabs: [
        { idx: 0, title: "upcoming", icon: mdiPlayCircleOutline },
        { idx: 1, title: "ride", icon: mdiAccountClock },
        { idx: 2, title: "missed", icon: mdiAccountOff },
        { idx: 3, title: "completed", icon: mdiAccountCheck },
        { idx: 4, title: "cancelled", icon: mdiMotionPause },
        { idx: 5, title: "الركاب غير الصاعدين", icon: mdiAccountCancelOutline },
      ],
      active_tab: null,
      icons: {
        mdiStopCircleOutline,
        mdiAccountCheck,
        mdiAccountOff,
        mdiAccountClock,
        mdiPlayCircleOutline,
        mdiTrashCan,
        mdiDeleteRestore,
        mdiAirplane
      },
    };
  },
  watch: {
    active_tab: function (newVal, oldVal) {
      localStorage.tabIdxReservations = newVal;
    },
  },
  computed: {
    translatedTabs() {
      return this.tabs.map(tab => ({
        ...tab,
        title: this.$te && this.$te(tab.title) ? this.$t(tab.title) : tab.title
      }));
    },
  },
  mounted() {
    this.active_tab = parseInt(localStorage.tabIdxReservations) || 0;
    this.loadReservations();
  },
  methods: {
    openCustomerModal(customer) {
      this.selectedCustomer = customer;
      this.showCustomerModal = true;
    },
    setQuickFilter(type) {
      this.quickFilter = type;
      const today = new Date();
      const formatYearMonthDay = (d) => d.toISOString().split('T')[0];

      if (type === 'today') {
        const todayStr = formatYearMonthDay(today);
        this.filters.date_from = todayStr;
        this.filters.date_to = todayStr;
      } else if (type === 'tomorrow') {
        const tomorrow = new Date(today);
        tomorrow.setDate(tomorrow.getDate() + 1);
        const tomorrowStr = formatYearMonthDay(tomorrow);
        this.filters.date_from = tomorrowStr;
        this.filters.date_to = tomorrowStr;
      } else if (type === 'all') {
        this.filters.date_from = "";
        this.filters.date_to = "";
      }
      this.applyFilters();
    },
    applyFilters() {
      this.loadReservations();
    },
    resetFilters() {
      this.filters = {
        customer_search: "",
        driver_id: "",
        date_from: "",
        date_to: ""
      };
      this.quickFilter = "all";
      this.loadReservations();
    },
    loadReservations() {
      this.isLoading = true;
      let queryParams = [];

      if (this.filters.customer_search) {
        queryParams.push(`customer_search=${encodeURIComponent(this.filters.customer_search)}`);
      }
      if (this.filters.driver_id) {
        queryParams.push(`driver_id=${encodeURIComponent(this.filters.driver_id)}`);
      }
      if (this.filters.date_from) {
        queryParams.push(`date_from=${encodeURIComponent(this.filters.date_from)}`);
      }
      if (this.filters.date_to) {
        queryParams.push(`date_to=${encodeURIComponent(this.filters.date_to)}`);
      }

      let url = '/reservations/all';
      if (queryParams.length > 0) {
        url += '?' + queryParams.join('&');
      }

      axios
        .get(url)
        .then((response) => {
          this.activeReservations = response.data.active || [];
          this.rideReservations = response.data.ride || [];
          this.completedReservations = response.data.completed || [];
          this.missedReservations = response.data.missed || [];
          this.cancelledReservations = response.data.cancelled || [];
        })
        .catch((error) => {
          this.$notify({
            title: "Error",
            text: this.$t("error_retrieving_reservations"),
            type: "error",
          });
          console.log(error);
          this.$swal("Error", error.response ? error.response.data.message : 'Error', "error");
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    cancelReservation(reservation, index) {
      let title = "";
      if(this.active_tab >= 1 && this.active_tab <= 3)
      {
        title = this.$t("cancel_reservation_warning");
      }
      else
      {
        title = this.$t("cancel_reservation_simple");
      }
      this.$swal({
        title: this.$t("are_you_sure"),
        text: title,
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: this.$t("yes_cancel_it"),
        cancelButtonText: this.$t("cancel"),
      }).then((result) => {
        if (result.isConfirmed) {
          axios
            .post(`/reservations/cancel`, {
              id: reservation.id,
              reason: "Cancelled by admin",
            })
            .then((response) => {
              this.$notify({
                title: "Success",
                text: this.$t("reservation_cancelled_successfully"),
                type: "success",
              });
              if(response.status == 201)
                this.$swal("Info", response.data.message, "info");
              let reservationToCancel = null
              if(this.active_tab == 0)
              {
                reservationToCancel = this.activeReservations[index];
                this.activeReservations.splice(index, 1);
              }
              else if(this.active_tab == 1)
              {
                reservationToCancel = this.rideReservations[index];
                this.rideReservations.splice(index, 1);
              }
              else if(this.active_tab == 2)
              {
                reservationToCancel = this.missedReservations[index];
                this.missedReservations.splice(index, 1);
              }
              else if(this.active_tab == 3)
              {
                reservationToCancel = this.completedReservations[index];
                this.completedReservations.splice(index, 1);
              }
              if (reservationToCancel) {
                this.cancelledReservations.push(reservationToCancel);
              }
            })
            .catch((error) => {
              this.$notify({
                title: "Error",
                text: this.$t("error_cancelling_reservation"),
                type: "error",
              });
              console.log(error);
              this.$swal("Error", error.response ? error.response.data.message : 'Error', "error");
            });
        }
      });
    },
  },
};
</script>

<style scoped>
.gap-1 { gap: 4px; }
.border-grey { border: 1px solid #e0e0e0 !important; }
</style>
